import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Invoice } from "interface/accounting";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";
import { FieldFull, FieldSplit, FieldRow } from "ui/CardField";
import { formatDate, formatAmount } from "utils/formatters";

interface InvoicesMobileListProps {
	items: Invoice[];
	loading: boolean;
	onOpenPdf: (item: Invoice) => void;
	onLoadMore: () => void;
}

const getStatusClassName = (item: Invoice) => {
	let _class = item.status === 1 ? "invoice-list-card--status-green" : "invoice-list-card--status-red";

	return _class;
};

const InvoicesMobileList: FC<InvoicesMobileListProps> = ({
	items,
	loading,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	return (
		<div className="invoice-list-mobile" onScroll={handleScroll}>
			{empty && <div className="invoice-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const itemKey = `${item.invoiceNumber}-${item.reportGuid ?? "noreport"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`invoice-list-card ${getStatusClassName(item)}`}
					>
						<div className="invoice-list-card-header">
							<div className="invoice-list-card-note"><strong>{`${item.invoiceType || ""} ${item.invoiceNumber || "-"}`.trim()}</strong></div>
							<div className="invoice-list-card-date">
								{item.invoiceDate ? (
									<GrideDateFormatter
										date={item.invoiceDate}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="invoice-list-card-body">
							<FieldRow
								items={[
									{ label: t("Buyer"), value: item.customerName || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: "Forfall", value: formatDate(item.dueDate) },
									{ label: "EHF", value: item.invoiceSentDate ? t("Yes") : t("No") },
								]}
							/>
							<FieldRow
								items={[
									{ label: "Beløp inkl. MVA", value: formatAmount(item.grossValue) },
									{ label: "Restbeløp", value: formatAmount(item.remainingValue) },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("PrintDate"), value: formatDate(item.printedDate) },
									{ label: t("PrintedBy"), value: item.printedBy || "-" },
								]}
							/>
						</div>

						<div className="invoice-list-card-actions">
							<button
								type="button"
								className="invoice-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img src={`/icon/${ICON_NAMES.Download}.svg`} alt={t("Download")} />
							</button>
						</div>
					</div>
				);
			})}

			{loading && <div className="invoice-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default InvoicesMobileList;